/**
 * Stale-while-revalidate cache for lists the app shows again and again (ADR 0042): going back to a
 * page paints what was there a moment ago at once, then quietly replaces it with the fresh answer.
 *
 *   const { data, loading } = useSWR(ws ? `folders:${ws.id}:design` : null, () => api.folders(ws.id, 'design'));
 *
 * - `data` is the cached value (possibly a little old) while the request runs; `loading` is true
 *   only when there is nothing to show yet — that is when a skeleton belongs on screen.
 * - Requests for the same key are shared (two components, one round trip) and a value fetched less
 *   than `dedupeMs` ago is not fetched again.
 * - `mutate` changes the cached value immediately (optimistic UI) and returns an undo;
 *   `invalidate` marks keys stale so every mounted reader revalidates.
 * - Keys carry the workspace id, so switching workspace never shows another workspace's rows; the
 *   whole cache is dropped on sign-out (`clearSWR`, called by the session).
 * - Memory by default. A key read with `{ persist: true }` (the first screens' lists: หน้าแรก, ไฟล์,
 *   งาน, สารบรรณ, Solution Studio) is also kept on this device by lib/listStore.ts — per person and
 *   workspace, metadata only — so a full reload paints the last list at once (`hydrateSWR`). A 401
 *   or 403 for a key forgets it, in memory and on the device.
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

interface Entry {
  value: unknown;
  has: boolean;
  /** When `value` was fetched (0 = stale: revalidate on next use). */
  at: number;
  error: unknown;
  promise: Promise<unknown> | null;
  /** Bumped on every change, so snapshots are cheap to compare. */
  version: number;
}

const entries = new Map<string, Entry>();
/**
 * Which session the cache belongs to: bumped by clearSWR (sign-in, sign-out, account switch). A
 * fetch started under an older session never writes into the cache (memory or device) and its
 * caller gets an error instead of the value — the API client drops such answers too
 * (api/client.ts StaleSessionError); this is the second lock.
 */
let sessionEpoch = 0;

/** The answer belongs to a session that has ended (see `sessionEpoch`). */
export class StaleAnswerError extends Error {
  constructor() {
    super('บัญชีที่ใช้งานเปลี่ยนแล้ว คำตอบของคำขอเดิมจึงไม่ถูกแสดง');
    this.name = 'AbortError';
  }
}
/** Keys whose fresh values are also kept on this device (lib/listStore.ts installs the writer). */
const persisted = new Set<string>();
let persistence: { write: (key: string, value: unknown) => void; drop: (key: string) => void } | null = null;

/** lib/listStore.ts plugs itself in once loaded; values fetched before that are handed over now. */
export function setSWRPersistence(p: typeof persistence): void {
  persistence = p;
  if (p) for (const key of persisted) { const e = entries.get(key); if (e?.has && e.at > 0) p.write(key, e.value); }
}

function remember(key: string, e: Entry): void {
  if (persistence && persisted.has(key)) persistence.write(key, e.value);
}

/** Not allowed (any more), 401 / 403: what the cache holds for that key must not be shown again. */
export const refused = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401 || status === 403;
};

/**
 * Forget one key, in memory and on the device (the page answered 401/403: the person may not see
 * it any more). Readers on screen show their loading state and ask again. `prefix`: every key that
 * starts with `key` (all lists of one page of a workspace).
 */
export function forgetSWR(key: string, opts: { prefix?: boolean } = {}): void {
  const keys = opts.prefix ? [...entries.keys()].filter((k) => k.startsWith(key)) : [key];
  for (const k of keys) {
    const e = entries.get(k);
    if (e) {
      e.value = undefined;
      e.has = false;
      e.at = 0;
      emit(k);
    }
    persistence?.drop(k);
  }
}

/**
 * A value read back from this device after a reload: shown while the fresh one loads. Never
 * replaces a value this page already has (a fresh answer always wins), and is marked stale so the
 * first reader asks the server at once.
 */
export function hydrateSWR(key: string, value: unknown): void {
  const e = entryOf(key);
  if (e.has || e.promise) return;
  e.value = value;
  e.has = true;
  e.at = 0;
  e.error = null;
  persisted.add(key);
  emit(key);
}
const listeners = new Map<string, Set<() => void>>();
/** Bounded: the oldest keys go first (lists of other folders, old searches). */
const MAX_ENTRIES = 300;

function entryOf(key: string): Entry {
  let e = entries.get(key);
  if (!e) {
    e = { value: undefined, has: false, at: 0, error: null, promise: null, version: 0 };
    entries.set(key, e);
    if (entries.size > MAX_ENTRIES) {
      for (const k of entries.keys()) {
        if (entries.size <= MAX_ENTRIES) break;
        if (k !== key && !listeners.get(k)?.size && !entries.get(k)?.promise) entries.delete(k);
      }
    }
  }
  return e;
}

function emit(key: string): void {
  const e = entries.get(key);
  if (e) e.version++;
  listeners.get(key)?.forEach((fn) => fn());
}

/** The cached value, if any (never triggers a request). */
export function peekSWR<T>(key: string): T | undefined {
  const e = entries.get(key);
  return e?.has ? (e.value as T) : undefined;
}

/**
 * Put a value in the cache (e.g. the first page a page loaded itself). `persist`: a first-screen
 * list that is also kept on this device (see the header).
 */
export function primeSWR<T>(key: string, value: T, now = Date.now(), opts: { persist?: boolean } = {}): void {
  const e = entryOf(key);
  e.value = value;
  e.has = true;
  e.at = now;
  e.error = null;
  if (opts.persist) persisted.add(key);
  if (now > 0) remember(key, e);
  emit(key);
}

/**
 * Change a cached value now; returns a function that puts the previous value back (call it when
 * the request that was supposed to make the change fails).
 */
export function mutateSWR<T>(key: string, update: (current: T | undefined) => T): () => void {
  const e = entryOf(key);
  const before = { value: e.value, has: e.has, at: e.at };
  e.value = update(e.has ? (e.value as T) : undefined);
  e.has = true;
  remember(key, e);
  emit(key);
  return () => {
    Object.assign(e, before);
    if (e.has) remember(key, e);
    emit(key);
  };
}

/** Mark matching keys stale: readers on screen fetch again, everyone else on next use. */
export function invalidateSWR(match: string | ((key: string) => boolean)): void {
  const test = typeof match === 'string' ? (k: string) => k === match || k.startsWith(match) : match;
  for (const [key, e] of entries) {
    if (!test(key)) continue;
    e.at = 0;
    emit(key);
  }
}

/**
 * Change every cached value whose key matches, now (an item was deleted or moved: it must not
 * flash back from another list's cache when that page is opened). Keys without a value are skipped.
 */
export function updateSWR<T>(match: string | ((key: string) => boolean), update: (current: T, key: string) => T): void {
  const test = typeof match === 'string' ? (k: string) => k.startsWith(match) : match;
  for (const [key, e] of entries) {
    if (!e.has || !test(key)) continue;
    const next = update(e.value as T, key);
    if (next === e.value) continue;
    e.value = next;
    remember(key, e);
    emit(key);
  }
}

/** Forget everything (sign-out). What is kept on the device is cleared by the session (lib/listStore.ts). */
export function clearSWR(): void {
  sessionEpoch++;
  const keys = [...entries.keys()];
  entries.clear();
  persisted.clear();
  for (const k of keys) listeners.get(k)?.forEach((fn) => fn());
}

/** Fetch `key` unless a fresh value or a running request exists. Resolves with the value. */
export function fetchSWR<T>(key: string, fetcher: () => Promise<T>, opts: { dedupeMs?: number; force?: boolean; persist?: boolean } = {}): Promise<T> {
  const e = entryOf(key);
  if (opts.persist) persisted.add(key);
  if (e.promise) return e.promise as Promise<T>;
  if (!opts.force && e.has && Date.now() - e.at < (opts.dedupeMs ?? 2000)) return Promise.resolve(e.value as T);
  const askedIn = sessionEpoch;
  const p = fetcher().then(
    (value) => {
      // Asked for by a session that has ended (sign-out / another person): dropped, not cached, not returned.
      if (askedIn !== sessionEpoch) {
        e.promise = null;
        throw new StaleAnswerError();
      }
      // The entry may have been cleared (sign-out) while the request was in flight: do not resurrect it.
      if (entries.get(key) === e) {
        e.value = value;
        e.has = true;
        e.at = Date.now();
        e.error = null;
        e.promise = null;
        remember(key, e);
        emit(key);
      }
      return value;
    },
    (error: unknown) => {
      if (askedIn !== sessionEpoch) e.promise = null;
      else if (entries.get(key) === e) {
        e.error = error;
        e.promise = null;
        if (refused(error)) {
          // Not allowed any more: the old rows go, here and on the device.
          e.value = undefined;
          e.has = false;
          persistence?.drop(key);
        }
        emit(key);
      }
      throw error;
    },
  );
  e.promise = p;
  emit(key);
  return p;
}

export interface SWRResult<T> {
  /** The cached value — possibly older than the request that is running. */
  data: T | undefined;
  /** The last request failed (the stale `data`, if any, is still there). */
  error: unknown;
  /** Nothing to show yet: the first request for this key is running. */
  loading: boolean;
  /** A request is running (with or without data on screen). */
  validating: boolean;
  /** Fetch again now. */
  reload: () => Promise<void>;
  /** Optimistic change of this key's value; returns the undo. */
  mutate: (update: (current: T | undefined) => T) => () => void;
}

const noop = () => () => undefined;

export function useSWR<T>(key: string | null, fetcher: () => Promise<T>, opts: { dedupeMs?: number; persist?: boolean } = {}): SWRResult<T> {
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  const dedupeMs = opts.dedupeMs;
  const persist = !!opts.persist;

  const subscribe = useCallback(
    (fn: () => void) => {
      if (!key) return () => undefined;
      let set = listeners.get(key);
      if (!set) listeners.set(key, (set = new Set()));
      set.add(fn);
      return () => {
        set.delete(fn);
        if (!set.size) listeners.delete(key);
      };
    },
    [key],
  );
  // The version number changes whenever the entry does; the entry itself is read below.
  const version = useSyncExternalStore(key ? subscribe : noop, () => (key ? entries.get(key)?.version ?? -1 : -2));
  const e = key ? entries.get(key) : undefined;
  const stale = !!key && (!e || !e.has || e.at === 0);

  useEffect(() => {
    if (!key) return;
    void fetchSWR(key, () => fetchRef.current(), { dedupeMs, persist }).catch(() => undefined);
    // `stale` re-runs this after invalidateSWR; `version` is not a dependency (it would refetch on every change).
  }, [key, dedupeMs, stale, persist]);

  const reload = useCallback(async () => {
    if (key) await fetchSWR(key, () => fetchRef.current(), { force: true, persist }).catch(() => undefined);
  }, [key, persist]);
  const mutate = useCallback((update: (current: T | undefined) => T) => (key ? mutateSWR(key, update) : () => undefined), [key]);

  void version;
  return {
    data: e?.has ? (e.value as T) : undefined,
    error: e?.error ?? null,
    loading: !!key && !e?.has && !e?.error,
    validating: !!e?.promise,
    reload,
    mutate,
  };
}
